import React from 'react';
import { useModules } from '@/contexts/ModuleContext';
import { Badge } from '@/components/ui/badge';
import { Lock, Clock, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * ModuleGate - Wraps content that requires a specific module
 * 
 * If module is disabled by admin: Shows grayed out with "Demnächst" badge
 * If module requires higher plan: Shows with lock icon and upgrade prompt
 * If user has access: Shows content normally
 */
const ModuleGate = ({ 
  moduleId, 
  children, 
  fallback = null,
  showComingSoon = true,
  className = ''
}) => {
  const { checkModuleAccess, loading } = useModules();

  if (loading) {
    return fallback;
  }

  const access = checkModuleAccess(moduleId);

  // User has access - render children
  if (access.hasAccess) {
    return <>{children}</>;
  }

  // Module is disabled by admin - show "Coming Soon"
  if (!access.isEnabled && showComingSoon) {
    return (
      <div className={cn('relative', className)}>
        <div className="opacity-40 pointer-events-none blur-[1px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[2px] rounded-lg">
          <div className="text-center p-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              <Badge variant="secondary" className="text-sm">
                Demnächst
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {access.comingSoonText || 'Dieses Modul wird bald verfügbar sein'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Module is enabled but user needs higher plan
  if (access.isEnabled && !access.hasAccess) {
    return (
      <div className={cn('relative', className)}>
        <div className="opacity-40 pointer-events-none blur-[1px]">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[2px] rounded-lg">
          <div className="text-center p-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Lock className="w-5 h-5 text-amber-500" />
              <Badge variant="outline" className="text-sm border-amber-500 text-amber-600">
                <Sparkles className="w-3 h-3 mr-1" />
                {access.requiredPlan === 'business' ? 'Business' : 'Pro'}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Upgrade auf {access.requiredPlan === 'business' ? 'Business' : 'Pro'} um dieses Feature zu nutzen
            </p>
          </div>
        </div>
      </div>
    );
  }

  return fallback;
};

export default ModuleGate;
